import { ChildSubtab, TabRoute } from 'src/components/CommonV2Components/GenericSideBar/types';

/**
 * Finds the first visible (non-hidden) subtab for a given route
 */
const getFirstVisibleSubtab = (route: TabRoute): ChildSubtab | undefined => {
  return route.childSubtabs?.find((subtab) => !subtab.hidden);
};

/**
 * Finds a route that matches the given tab and has at least one visible subtab
 */
const findRouteByTabWithVisibleSubtab = (
  childRoutes: TabRoute[],
  currentTab: string
): { route: TabRoute; subtab: ChildSubtab } | undefined => {
  for (const route of childRoutes) {
    if (route.hidden) continue;

    const matchesTab =
      route.tabMappedFrom === currentTab ||
      route.childSubtabs?.some((subtab) => subtab.parentTab === currentTab);

    if (matchesTab) {
      const firstVisibleSubtab = getFirstVisibleSubtab(route);
      if (firstVisibleSubtab) {
        return { route, subtab: firstVisibleSubtab };
      }
    }
  }
  return undefined;
};

/**
 * Takes in the location from React Router and a collection of tab routes and returns the active route based on the URL.
 */
export const findCurrentTabAndSubtab = ({ location, childRoutes }) => {
  let currentRoute: TabRoute; // The tab route object we return at the end
  let currentSubtab: string; // The selected subtab as a string if applicable

  const currentParams = new URLSearchParams(location.search);
  const currentTab = currentParams.get('tab');
  currentSubtab = currentParams.get('subtab');

  /**
   * The purpose of the following logic is to map the old Beacon navigation to the new navigation components.
   * For each route object, we create an collection of the subtab values and subtab parent values.
   * The subtab values are the new subtabs per each new Beacon page. i.e. Key Metrics, Product Growth, Waterfall under the Summary sidebar tab.
   * The subtab parents are just the tabs that are used to render page layouts based on the old system. Using the previous example, this would NOT be "Summary" but "Sales".
   * We do 2 different checks: The first tries to determine a match based on tab and subtab values.
   * The second match is a fallback when the sidebar tab does not have child subtabs.
   */
  currentRoute = childRoutes.find((route: TabRoute) => {
    if (route.hidden) return false;

    const visibleSubtabs = route.childSubtabs?.filter((subtab) => !subtab.hidden) ?? [];
    const childSubtabValues = visibleSubtabs.map((subtab) => subtab.value);
    const childSubtabParentTabs = visibleSubtabs.map((subtab) => subtab.parentTab);

    return (
      (childSubtabValues.includes(currentSubtab) && childSubtabParentTabs.includes(currentTab)) ||
      (route.tabMappedFrom === currentTab && route.subtabMappedFrom === currentSubtab)
    );
  });

  if (!currentRoute) {
    // Fallback 1: Try to find a route matching the current tab with any visible subtab
    const tabMatch = findRouteByTabWithVisibleSubtab(childRoutes, currentTab);
    if (tabMatch) {
      currentRoute = tabMatch.route;
      currentSubtab = tabMatch.subtab.value;
    } else {
      // Fallback 2: Use the first non-hidden route with a visible subtab
      const firstVisibleRoute = childRoutes.find(
        (route: TabRoute) => !route.hidden && getFirstVisibleSubtab(route)
      );
      if (firstVisibleRoute) {
        currentRoute = firstVisibleRoute;
        currentSubtab = getFirstVisibleSubtab(firstVisibleRoute)?.value ?? firstVisibleRoute.childSubtabs[0].value;
      } else {
        // Final fallback: Use the first route (original behavior)
        [currentRoute] = childRoutes;
        currentSubtab = currentRoute.childSubtabs[0].value;
      }
    }
  }

  return { currentRoute, currentSubtab };
};
